/**
 * Format a numeric-ish value as USD currency, e.g. 1234.5 -> "$1,234.50".
 * Non-numeric input is returned as a plain string (blank for null/undefined).
 */
export function formatCurrency(value: any): string {
  const n = Number(value);
  if (Number.isFinite(n)) {
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return value == null ? '' : String(value);
}

/**
 * Parse display-formatted USD text back to a number, for the Excel export.
 *
 * Accepts the shapes the app renders: "$1,234.50", "$-250.00" (formatCurrency),
 * "-$250.00" (Intl currency), "($250.00)" (accounting), with or without
 * thousands separators / decimals. Anything else — including a bare digit
 * string such as a zip code or container number — returns null so it stays
 * text and keeps its leading zeros. `decimals` is how many the text carried,
 * so the caller can pick a matching number format.
 */
export function parseCurrencyText(text: string): { value: number; decimals: number } | null {
  const s = text.trim();
  const m = /^(\()?\s*(-)?\s*\$\s*(-)?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s*(\))?$/.exec(s);
  if (!m) return null;
  const [, open, neg1, neg2, intPart, frac, close] = m;
  // Parentheses must be balanced, and a parenthesized value carries no sign of its own.
  if (!!open !== !!close) return null;
  if (open && (neg1 || neg2)) return null;
  if (neg1 && neg2) return null;
  const n = Number(intPart.replace(/,/g, '') + (frac ? `.${frac}` : ''));
  if (!Number.isFinite(n)) return null;
  const negative = !!open || !!neg1 || !!neg2;
  return { value: negative ? -n : n, decimals: frac ? frac.length : 0 };
}

/**
 * Format a numeric-ish value as whole US dollars, e.g. 340592.03 -> "$340,592".
 * Non-numeric input is returned as a plain string (blank for null/undefined).
 */
export function formatWholeDollars(value: any): string {
  const n = Number(value);
  if (Number.isFinite(n)) {
    return `$${Math.round(n).toLocaleString()}`;
  }
  return value == null ? '' : String(value);
}

/**
 * A quote rate as the quote itself states it: up to 5 decimals, trailing zeros trimmed to
 * a 2-decimal minimum. This is v1's `padrate` (sprintf '%.05f', then trimmed).
 *
 * Both places that display a stored dray rate used to round it to whole dollars for
 * "display only": the quote's Drayage & Fuel panel and the PRO's Quote Charges panel. A
 * rate of 321.10 therefore read as "321" on both, while the quote's own Total w/Charges
 * read 321.10 — and on the PRO, the row can be flagged a rate MISMATCH while showing a
 * figure that looks identical to the invoice's, because the mismatch test uses the true
 * value and only the display was rounded.
 *
 * Whole numbers keep 2 decimals (321 -> "321.00") so a column of rates aligns; a per-mile
 * rate keeps its precision (0.458 -> "0.458") rather than collapsing to "0.46", which
 * matters because that rate is multiplied by the mileage.
 */
export function formatQuoteRate(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  // A non-numeric string is the quote's own raw field and passes through, as the rounding
  // helper this replaced did. A non-finite NUMBER has nothing to show, and "NaN" on a rate
  // line is worse than a blank.
  if (!Number.isFinite(n)) return typeof value === 'string' ? value : '';
  const [int, frac] = Math.abs(n).toFixed(5).split('.');
  const kept = frac.slice(0, 2) + frac.slice(2).replace(/0+$/, '');
  return `${n < 0 ? '-' : ''}${Number(int).toLocaleString()}.${kept}`;
}
